import { isEqual } from 'lodash';
import * as Sentry from '@sentry/browser';

import defaultConfig from '../../runtime-config.json';

import { createReducer } from 'utils/redux';
import { API, get } from 'utils/api';
import { logError } from 'utils/log';
import { camelCaseKeys } from 'utils/keys';
import { ITEMS_PER_OUTFIT } from 'utils/realm-outfits';

import type { Action, ThunkAction } from 'types';

const UPDATE_TIMEOUT = 5 * 60 * 1000;

const CONFIG_URL = API.END_POINTS.API.CONFIG;

const CONFIG_UPDATED = 'runtimeConfig/CONFIG_UPDATED';

const initialState = {
  config: {}
};

const defaultConfigKeys = Object.keys(camelCaseKeys(defaultConfig));

let timerId;

function configUpdated(config): Action {
  return {
    type: CONFIG_UPDATED,
    payload: {
      config
    }
  };
}

export function resetConfig(): void {
  clearTimeout(timerId);
}

export function fetchConfig(useMock?: boolean): ThunkAction {
  return function loadConfig(dispatch, getState) {
    if (useMock) {
      return dispatch(configUpdated(defaultConfig));
    }

    return get(`${CONFIG_URL}?timestamp=${Date.now()}`, {}, { suppressGlobalNotification: true, suppressAuth: true, suppressCollabHeaders: true })
      .then((response: any) => {
        const runtimeConfig = getState().runtimeConfig;
        const newVersionReleased = getState()?.version?.newVersionReleased ?? false;
        const config = response.data;
        const hasConfigUpdated = !isEqual(runtimeConfig.config, config);

        if (hasConfigUpdated) {
          dispatch(configUpdated(response.data));

          if (config?.realmOutfitsMaxItems && config?.realmOutfitsMinItems) {
            ITEMS_PER_OUTFIT.set({
              min: config?.realmOutfitsMinItems,
              max: config?.realmOutfitsMaxItems
            });
          }

          const configKeys = Object.keys(config);
          const missingKeys = defaultConfigKeys.filter(key => !configKeys.includes(key));

          if (missingKeys.length && window.app.env.IS_PROD && !newVersionReleased) {
            Sentry.captureException(`Missing runtime config keys in production - ${missingKeys.toString()}`);
          }
        }
      })
      .catch(error => {
        logError(error);
      })
      .then(() => {
        timerId = setTimeout(() => dispatch(loadConfig), UPDATE_TIMEOUT);
      });
  };
}

export default createReducer<any, any>('runtimeConfig', initialState, {
  [CONFIG_UPDATED]: (state, { config }) => ({
    config: {
      ...config,
      mapByLightweightProfileMaintainerIds: (config.lightweightProfileMaintainerIds || []).reduce((res, id) => {
        res[id] = true;
        return res;
      }, {}),
      mapBySovTimeframeCanSelectPastTwoWeeksIds: (config.sovTimeframeCanSelectPastTwoWeeksIds || []).reduce((res, id) => {
        res[id] = true;
        return res;
      }, {}),
      mapByInsightsReportsEarlyAccessIds: (config.insightsReportsEarlyAccessIds || []).reduce((res, id) => {
        res[id] = true;
        return res;
      }, {}),
      mapByDisableUnreadMessages: (config.disableUnreadMessages || []).reduce((res, id) => {
        res[id] = true;
        return res;
      }, {})
    }
  })
});
